import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

export default function StudioCustomerDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id');

  const [activeTab, setActiveTab] = useState<'ordersheet' | 'checklist' | 'delivery'>('ordersheet');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // 촬영 오더시트 데이터
  const [orderSheet, setOrderSheet] = useState({
    customerName: '김지은',
    contact: '010-1234-5678',
    shootingDate: '2024-01-25',
    shootingTime: '10:00',
    shootingType: '리허설',
    concept: '내추럴',
    location: '스튜디오 A',
    photographer: '박작가',
    assistant: '김보조',
    shotList: '전신샷, 상반신샷, 클로즈업, 커플샷',
    familyPhoto: true,
    familyCount: '4명',
    dressInfo: '화이트 A라인 드레스',
    suitInfo: '네이비 턱시도',
    makeupStyle: '내추럴 메이크업',
    hairStyle: '웨이브 다운스타일',
    studioSetting: '화이트 배경, 소품: 꽃다발',
    specialRequest: '야외 촬영 포함 요청',
    memo: '고객 요청사항 많음, 시간 여유있게 진행'
  });

  // 체크리스트 데이터
  const [checklist, setChecklist] = useState([
    { id: 1, item: '의상/드레스 픽업 확인', checked: true },
    { id: 2, item: '촬영 소품 준비', checked: true },
    { id: 3, item: '메이크업 완료 확인', checked: false },
    { id: 4, item: '스튜디오 룸 세팅', checked: true },
    { id: 5, item: '가족 인원 확인', checked: true },
    { id: 6, item: '고객 요청사항 체크', checked: false },
    { id: 7, item: '촬영 장비 점검', checked: true },
    { id: 8, item: '조명 세팅 완료', checked: false }
  ]);

  // 결과물 전달 데이터
  const [deliveryData, setDeliveryData] = useState({
    totalPhotos: 150,
    selectedPhotos: 30,
    retouchedPhotos: 25,
    downloadLink: 'https://studio.example.com/download/abc123',
    expiryDate: '2024-03-25',
    downloadCount: 2,
    maxDownloads: 5,
    status: '선택대기'
  });

  const handleChecklistToggle = (id: number) => {
    setChecklist(checklist.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const handleGenerateLink = () => {
    alert('다운로드 링크가 생성되었습니다!');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard/studio-customers')}
              className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{orderSheet.customerName} 님 촬영 정보</h1>
              <p className="text-sm text-gray-600 mt-1">촬영 오더시트 및 진행 상황을 관리하세요</p>
            </div>
          </div>
          <button className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer">
            <i className="ri-printer-line"></i>
            오더시트 출력
          </button>
        </div>

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('ordersheet')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'ordersheet'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              촬영 오더시트
            </button>
            <button
              onClick={() => setActiveTab('checklist')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'checklist'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-checkbox-line mr-2"></i>
              사전 준비 체크리스트
            </button>
            <button
              onClick={() => setActiveTab('delivery')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'delivery'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-download-cloud-line mr-2"></i>
              결과물 전달 관리
            </button>
          </div>
        </div>

        {/* 촬영 오더시트 */}
        {activeTab === 'ordersheet' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-6">촬영 오더시트 (촬영 지시서)</h2>
            
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">기본 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">고객명</label>
                    <input
                      type="text"
                      value={orderSheet.customerName}
                      onChange={(e) => setOrderSheet({...orderSheet, customerName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">연락처</label>
                    <input
                      type="text"
                      value={orderSheet.contact}
                      onChange={(e) => setOrderSheet({...orderSheet, contact: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">촬영일</label>
                    <input
                      type="date"
                      value={orderSheet.shootingDate}
                      onChange={(e) => setOrderSheet({...orderSheet, shootingDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">촬영시간</label>
                    <input
                      type="time"
                      value={orderSheet.shootingTime}
                      onChange={(e) => setOrderSheet({...orderSheet, shootingTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">촬영 종류</label>
                    <input
                      type="text"
                      value={orderSheet.shootingType}
                      onChange={(e) => setOrderSheet({...orderSheet, shootingType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">촬영 컨셉</label>
                    <input
                      type="text"
                      value={orderSheet.concept}
                      onChange={(e) => setOrderSheet({...orderSheet, concept: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 촬영 정보 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">촬영 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">촬영 장소</label>
                    <input
                      type="text"
                      value={orderSheet.location}
                      onChange={(e) => setOrderSheet({...orderSheet, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">담당 작가</label>
                    <input
                      type="text"
                      value={orderSheet.photographer}
                      onChange={(e) => setOrderSheet({...orderSheet, photographer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">보조 인력</label>
                    <input
                      type="text"
                      value={orderSheet.assistant}
                      onChange={(e) => setOrderSheet({...orderSheet, assistant: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">원하는 샷 리스트</label>
                    <input
                      type="text"
                      value={orderSheet.shotList}
                      onChange={(e) => setOrderSheet({...orderSheet, shotList: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 가족사진 정보 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">가족사진 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderSheet.familyPhoto}
                        onChange={(e) => setOrderSheet({...orderSheet, familyPhoto: e.target.checked})}
                        className="w-4 h-4 text-blue-600 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">가족사진 촬영</span>
                    </label>
                  </div>
                  {orderSheet.familyPhoto && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">가족 인원</label>
                      <input
                        type="text"
                        value={orderSheet.familyCount}
                        onChange={(e) => setOrderSheet({...orderSheet, familyCount: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 의상 정보 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">의상 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">드레스 정보</label>
                    <input
                      type="text"
                      value={orderSheet.dressInfo}
                      onChange={(e) => setOrderSheet({...orderSheet, dressInfo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">수트 정보</label>
                    <input
                      type="text"
                      value={orderSheet.suitInfo}
                      onChange={(e) => setOrderSheet({...orderSheet, suitInfo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">메이크업 스타일</label>
                    <input
                      type="text"
                      value={orderSheet.makeupStyle}
                      onChange={(e) => setOrderSheet({...orderSheet, makeupStyle: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">헤어 스타일</label>
                    <input
                      type="text"
                      value={orderSheet.hairStyle}
                      onChange={(e) => setOrderSheet({...orderSheet, hairStyle: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 스튜디오 세팅 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">스튜디오 세팅</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">세팅 메모</label>
                  <textarea
                    value={orderSheet.studioSetting}
                    onChange={(e) => setOrderSheet({...orderSheet, studioSetting: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                  />
                </div>
              </div>

              {/* 특이사항 */}
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">특이사항 및 메모</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">고객 특별 요청</label>
                    <textarea
                      value={orderSheet.specialRequest}
                      onChange={(e) => setOrderSheet({...orderSheet, specialRequest: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">내부 메모</label>
                    <textarea
                      value={orderSheet.memo}
                      onChange={(e) => setOrderSheet({...orderSheet, memo: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                  저장
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 사전 준비 체크리스트 */}
        {activeTab === 'checklist' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-gray-800">사전 준비 체크리스트</h2>
              <div className="text-sm text-gray-600">
                완료: {checklist.filter(item => item.checked).length} / {checklist.length}
              </div>
            </div>
            
            <div className="space-y-3">
              {checklist.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleChecklistToggle(item.id)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center cursor-pointer ${
                    item.checked ? 'bg-green-500' : 'bg-gray-200'
                  }`}>
                    {item.checked && <i className="ri-check-line text-white"></i>}
                  </div>
                  <span className={`flex-1 ${item.checked ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                    {item.item}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-xl"></i>
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">촬영 전날 자동 알림</p>
                  <p className="text-sm text-blue-700">촬영 전날 오후 6시에 미완료 항목에 대한 알림이 자동으로 발송됩니다.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 결과물 전달 관리 */}
        {activeTab === 'delivery' && (
          <div className="space-y-6">
            {/* 업로드 현황 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-800">사진/영상 업로드 현황</h2>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-line mr-2"></i>
                  파일 업로드
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">전체 사진</div>
                  <div className="text-2xl font-bold text-gray-800">{deliveryData.totalPhotos}장</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">고객 선택본</div>
                  <div className="text-2xl font-bold text-blue-600">{deliveryData.selectedPhotos}장</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">리터칭 완료</div>
                  <div className="text-2xl font-bold text-green-600">{deliveryData.retouchedPhotos}장</div>
                </div>
              </div>
            </div>

            {/* 다운로드 링크 관리 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-6">다운로드 링크 관리</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">다운로드 링크</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={deliveryData.downloadLink}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(deliveryData.downloadLink);
                        alert('링크가 복사되었습니다!');
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-copy-line mr-2"></i>
                      복사
                    </button>
                    <button
                      onClick={handleGenerateLink}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      재생성
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">만료일</label>
                    <input
                      type="date"
                      value={deliveryData.expiryDate}
                      onChange={(e) => setDeliveryData({...deliveryData, expiryDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">다운로드 횟수</label>
                    <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                      {deliveryData.downloadCount} / {deliveryData.maxDownloads}회
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">전달 상태</label>
                  <select
                    value={deliveryData.status}
                    onChange={(e) => setDeliveryData({...deliveryData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-8 cursor-pointer"
                  >
                    <option value="대기중">대기중</option>
                    <option value="선택대기">선택대기</option>
                    <option value="리터칭중">리터칭중</option>
                    <option value="전달완료">전달완료</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line text-yellow-600 text-xl"></i>
                  <div>
                    <p className="text-sm font-medium text-yellow-900 mb-1">링크 보안 안내</p>
                    <p className="text-sm text-yellow-700">다운로드 링크는 만료일 이후 자동으로 비활성화되며, 최대 다운로드 횟수 초과 시 접근이 제한됩니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 파일 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">사진/영상 업로드</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-upload-cloud-line text-3xl text-blue-600"></i>
              </div>
              <p className="text-gray-700 mb-2">파일을 드래그하거나 클릭하여 업로드</p>
              <p className="text-sm text-gray-500 mb-4">JPG, PNG, MP4 파일 (최대 500MB)</p>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                파일 선택
              </button>
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 cursor-pointer" />
                <span className="text-sm text-gray-700">원본 파일로 업로드</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}